import { Alert, Button, Card, Spin, Typography } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { API_BASE_URL } from '../api/baseUrl';
import { renderWxLogin } from '../utils/wxLogin';
import { resolveErrorMessage } from '../utils/errorMessage';
import logoBig from '../assets/images/logo_big.png';
import './Login.css';

const normalizeReturnTo = (value) => {
  const s = String(value || '').trim();
  if (!s) return '/content';
  if (!s.startsWith('/')) return '/content';
  if (s.startsWith('//')) return '/content';
  return s;
};

const Login = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, refreshMe } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const returnTo = useMemo(() => {
    const params = new URLSearchParams(location.search || '');
    return normalizeReturnTo(params.get('return_to'));
  }, [location.search]);

  const wechatCallbackParams = useMemo(() => {
    const params = new URLSearchParams(location.search || '');
    const code = String(params.get('code') || '').trim();
    const state = String(params.get('state') || '').trim();
    return {
      code: code || '',
      state: state || '',
      hasCallback: Boolean(code && state),
    };
  }, [location.search]);

  const initQr = async () => {
    setError('');
    setLoading(true);
    try {
      const url = `${API_BASE_URL}/auth/wechat/qr_params?return_to=${encodeURIComponent(returnTo)}`;
      const resp = await fetch(url, { method: 'GET', credentials: 'include' });
      if (!resp.ok) throw new Error(`获取扫码参数失败（${resp.status}）`);
      const contentType = String(resp.headers.get('content-type') || '');
      if (!contentType.includes('application/json')) {
        const text = await resp.text();
        throw new Error(`获取扫码参数返回非 JSON（可能是 Nginx 未代理该接口或后端未更新）：${text.slice(0, 120)}`);
      }
      const data = await resp.json();
      if (data?.status !== 'success') throw new Error(resolveErrorMessage(data?.error_message, '获取扫码参数失败'));
      if (!data?.appid) throw new Error('后端未配置 WECHAT_OPEN_APPID，无法生成二维码');

      await renderWxLogin({
        containerId: 'wx_login_qr',
        appid: data.appid,
        scope: data.scope,
        redirectUri: data.redirect_uri,
        state: data.state,
        // Prefer WeChat's "fast login" button inside the embedded component:
        // users only need to click once (inside WxLogin) to complete login.
        fastLogin: 1,
        styleLite: 1,
      });
    } catch (e) {
      setError(e?.message || String(e));
    } finally {
      setLoading(false);
    }
  };

  const completeLogin = async ({ code, state }) => {
    setError('');
    setLoading(true);
    try {
      const resp = await fetch(`${API_BASE_URL}/auth/wechat/complete`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          channel: 'open',
          code,
          state,
          return_to: returnTo,
        }),
      });
      if (!resp.ok) {
        const text = await resp.text();
        throw new Error(`登录失败（${resp.status}）：${text.slice(0, 180)}`);
      }
      const data = await resp.json();
      if (data?.status !== 'success') throw new Error(resolveErrorMessage(data?.error_message, '登录失败'));

      await refreshMe({ silent: true });
      navigate(data?.return_to || returnTo, { replace: true });
    } catch (e) {
      setError(e?.message || String(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) {
      navigate(returnTo, { replace: true });
      return;
    }
    if (wechatCallbackParams.hasCallback) {
      completeLogin({ code: wechatCallbackParams.code, state: wechatCallbackParams.state }).catch((e) => {
        setError(e?.message || String(e));
      });
      return () => {};
    }

    return () => {};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [returnTo, user, wechatCallbackParams.hasCallback, wechatCallbackParams.code, wechatCallbackParams.state]);

  useEffect(() => {
    if (user) return;
    if (wechatCallbackParams.hasCallback) return;
    initQr().catch((e) => setError(e?.message || String(e)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [returnTo, user, wechatCallbackParams.hasCallback]);

  return (
    <div className="login-page">
      <Card className="login-card">
        <div className="login-header">
          <Typography.Title level={3} className="login-title">
            微信登录
          </Typography.Title>
          <Typography.Paragraph type="secondary" className="login-subtitle">
            选择“微信快捷登录”或扫码完成登录
          </Typography.Paragraph>
        </div>

        {error && (
          <Alert
            type="error"
            showIcon
            message="初始化失败"
            description={
              <>
                <div>{error}</div>
                <div style={{ marginTop: 8 }}>
                  <Button
                    type="link"
                    onClick={() => {
                      window.location.href = `${API_BASE_URL}/auth/wechat/login?channel=open&return_to=${encodeURIComponent(returnTo)}`;
                    }}
                    style={{ padding: 0, height: 'auto' }}
                  >
                    改用跳转登录
                  </Button>
                </div>
              </>
            }
            style={{ marginBottom: 16 }}
          />
        )}

        <Spin spinning={loading}>
          <div className="login-brand">
            <img src={logoBig} alt="AIspire" />
          </div>
          <div className="wx-login-wrap">
            <div id="wx_login_qr" className="wx-login" />
          </div>
        </Spin>
      </Card>
    </div>
  );
};

export default Login;
